'use client';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import Button from './Button';

interface StickyCtaProps {
  title: string;
  subtitle: string;
  ctaText: string;
  ctaHref: string;
  showAfterScroll?: number;
}

export default function StickyCTA({
  title,
  subtitle,
  ctaText,
  ctaHref,
  showAfterScroll = 800,
}: StickyCtaProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > showAfterScroll);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAfterScroll]);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-40 bg-zinc-900 border-t border-zinc-700 transition-all duration-500 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      } ${prefersReducedMotion ? 'transition-none' : ''}`}
      style={{
        willChange: isVisible ? 'auto' : 'transform',
      }}
    >
      <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 py-4 px-6">
        <div>
          <p className="text-h5 text-white">{title}</p>
          <p className="text-body-sm text-gray-300">{subtitle}</p>
        </div>
        <Link href={ctaHref} className="shrink-0">
          <Button variant="primary" size="md" className="border-glow whitespace-nowrap px-6 py-2">
            {ctaText}
          </Button>
        </Link>
      </div>
    </div>
  );
}